import { AngularLayout, FlexLayout } from '@rhtml/modifiers';
import { Component, css, html, LitElement } from '@rxdi/lit-html';
import { DividerComponent } from '@rxdi/ui-kit/divider';
import { ReactivumTheme } from '@shared/styles/theme';

/**
 * @customElement showcase-component
 */
@Component({
  selector: 'showcase-component',
  components: [DividerComponent],
  styles: [
    css`
      ${ReactivumTheme}

      :host {
        display: block;
        color: var(--text-primary);
      }

      .section-header {
        text-align: center;
        margin-bottom: 64px;
      }

      rx-divider {
        margin: 24px auto;
        width: 80px;
      }

      .section-description {
        color: var(--text-secondary);
        font-size: 1.125rem;
        line-height: 1.7;
        max-width: 600px;
        margin: 0 auto;
        font-weight: 400;
      }

      .tech-stack-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 32px;
        max-width: 1200px;
        margin: 0 auto 64px;
      }

      .tech-stack-icon {
        width: 48px;
        height: 48px;
        margin-bottom: 24px;
        color: var(--primary-color);
        opacity: 0.9;
      }

      .showcase-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 32px;
        max-width: 1200px;
        margin: 0 auto;
      }

      @media (max-width: 768px) {
        .tech-stack-grid,
        .showcase-grid {
          grid-template-columns: 1fr;
          gap: 24px;
        }
      }
    `,
  ],
  modifiers: [...FlexLayout, ...AngularLayout],
  template() {
    return html`
      <section class="section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">Our Technology Stack</h2>
            <rx-divider></rx-divider>
            <p class="section-description">
              We leverage cutting-edge technologies to deliver exceptional web applications with outstanding
              performance, scalability, and maintainability.
            </p>
          </div>

          <div class="tech-stack-grid">
            <div class="card">
              <div class="tech-stack-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <h3 class="card-title">Modern Frameworks</h3>
              <p class="card-description">
                We use the latest web technologies including @rhtml, @rxdi, and @gapi to build performant and scalable
                applications with modern development practices.
              </p>
            </div>

            <div class="card">
              <div class="tech-stack-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 class="card-title">Type Safety</h3>
              <p class="card-description">
                Full TypeScript integration ensures type safety across the entire stack, reducing bugs and improving
                developer experience.
              </p>
            </div>

            <div class="card">
              <div class="tech-stack-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 class="card-title">Performance</h3>
              <p class="card-description">
                Optimized for speed with serverless architecture, efficient bundling, and minimal bundle sizes for
                lightning-fast applications.
              </p>
            </div>
          </div>

          <div class="section-header">
            <h2 class="section-title">Showcase Projects</h2>
            <rx-divider></rx-divider>
            <p class="section-description">
              Discover how our frameworks and tools power real-world applications with exceptional performance and
              developer experience.
            </p>
          </div>

          <div class="showcase-grid">
            <div class="card">
              <h3 class="card-title">graphql-server.com</h3>
              <p class="card-description">
                Our flagship platform combining Agile/Kanban project management with instant GraphQL/REST backend
                generation using custom serverless architecture.
              </p>
            </div>

            <div class="card">
              <h3 class="card-title">@rhtml Framework</h3>
              <p class="card-description">
                The world's smallest dependency injection system at less than 1.4kb, featuring modern web components and
                declarative programming patterns.
              </p>
            </div>

            <div class="card">
              <h3 class="card-title">@gapi GraphQL</h3>
              <p class="card-description">
                Powerful GraphQL API framework with decorators, guards, and comprehensive TypeScript support for complex
                backend applications.
              </p>
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class ShowcaseComponent extends LitElement {}
